import _ from 'lodash';
import React from 'react';
import { removeTypename } from 'shared/helpers/utils';
import {
  getAcceptedPaymentOptions,
  getAcceptedPaymentOptionsV2,
  getPaymentFeeForMethod,
  getFirstAvailablePickupOption,
} from 'shared/helpers/dispensaries';
import {
  paymentMethodsToDisplayNames,
  orderTypesToDisplayNames,
  orderTypesToDisplayNamesV2,
  ORDER_TYPE_DELIVERY,
} from 'shared/constants';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import useCart from './use-cart';

// helpers
const getFeeLabel = (paymentType, paymentFees) => {
  const feeInfo = getPaymentFeeForMethod(paymentType, paymentFees) || {};
  const { feeType, fee } = feeInfo;
  const feeLabel = fee ? ('sublabel', feeType === 'cents' ? `($${(fee / 100).toFixed(2)} fee)` : `(${fee}% fee)`) : '';

  return feeLabel;
};
const getValidOrderTypesList = (orderTypesConfig, paymentTypeKey) => {
  const validOrderTypes = [];
  _.forEach(removeTypename(orderTypesConfig), (orderTypeConfig, orderTypeKey) => {
    if (orderTypeConfig.enabled && orderTypeKey !== 'kiosk' && orderTypeConfig.paymentTypes[paymentTypeKey]) {
      validOrderTypes.push(orderTypesToDisplayNames[orderTypeKey]);
    }
  });
  return validOrderTypes;
};

const getValidOrderTypesListV2 = (orderTypesConfigV2, paymentTypeKey) => {
  const validOrderTypes = [];
  _.forEach(removeTypename(orderTypesConfigV2), (orderTypeConfig, orderTypeKey) => {
    const orderTypeIsEnabled = orderTypeConfig.enableASAPOrdering || orderTypeConfig.enableScheduledOrdering;
    if (orderTypeIsEnabled && orderTypeKey !== 'kiosk' && orderTypeConfig.paymentTypes[paymentTypeKey]) {
      // Stripping out 'Pickup' string below since the V2 order types are longer/more numerous
      validOrderTypes.push(orderTypesToDisplayNamesV2[orderTypeKey].replace(' Pickup', ''));
    }
  });
  return validOrderTypes;
};

const getPaymentMethodLabel = (paymentTypeKey) => paymentMethodsToDisplayNames[paymentTypeKey];

const getPaymentMethodValue = (paymentTypeKey) => {
  // handles conversion of credit/debit types to appropriate value, if applicable
  if (paymentTypeKey === 'creditCardAtDoor') {
    return 'creditCard';
  }
  if (paymentTypeKey === 'debit') {
    return 'debitCard';
  }

  return paymentTypeKey;
};

export default function useDispensaryPaymentMethods() {
  const Cart = useCart();
  const { order, isKioskOrder } = Cart;
  const { dispensary } = order;

  const useOrderTypesConfigV2 = useDispensaryFlag('rollout.use-updated-ordering-configurations', dispensary?.id);

  if (!dispensary) {
    return null;
  }

  const { paymentFees } = dispensary;

  const v2OrderType = !Cart.orderType || Cart.orderType === 'pickup' ? 'inStorePickup' : Cart.orderType;
  const orderTypesConfig = useOrderTypesConfigV2
    ? _.omit(dispensary.orderTypesConfigV2, ['offerAnyPickupService', 'offerDeliveryService'])
    : dispensary.orderTypesConfig;

  const paymentTypeKeys = [
    'dutchiePay',
    'cash',
    'check',
    'creditCardAtDoor',
    'creditCardByPhone',
    'debit',
    'alt36',
    'linx',
    'canPay',
    'payOnlineHypur',
    'paytender',
    'aeropay',
    'payOnlineMerrco',
    'payOnlineMoneris',
    'payOnlineChase',
  ];

  const paymentMethodsToBeDisplayed = _.filter(paymentTypeKeys, (paymentTypeKey) => {
    // determine list of payment types that should be shown at checkout based on current order type.
    // For regular checkout, ALL dispo payment types for pickups/delivery should be visible, and
    // enabled/disabled as appropriate. For kiosk, only kiosk payment types should show.

    if (isKioskOrder) {
      return dispensary.orderTypesConfig.kiosk.paymentTypes[paymentTypeKey];
    }
    // it's regular checkout; match against payment type list with kiosk-only types excluded.
    // We don't want to show a payment type at regular checkout if it's not usable for any pickup/delivery option.
    return useOrderTypesConfigV2
      ? getAcceptedPaymentOptionsV2(dispensary)[paymentTypeKey]
      : getAcceptedPaymentOptions(dispensary)[paymentTypeKey];
  });

  const decoratedPaymentMethods = _.map(paymentMethodsToBeDisplayed, (paymentTypeKey) => {
    // add fee labelling
    const feeLabel = getFeeLabel(paymentTypeKey, paymentFees);
    let sublabel = feeLabel;

    // check if payment type is valid for currently-selected order type
    const orderType = useOrderTypesConfigV2
      ? v2OrderType
      : Cart.orderType ||
        (dispensary.offerAnyPickupService ? getFirstAvailablePickupOption(dispensary) : ORDER_TYPE_DELIVERY);

    const validPaymentTypes = orderTypesConfig[isKioskOrder ? 'kiosk' : orderType].paymentTypes;
    const disabledForSelectedOrderType = !validPaymentTypes[paymentTypeKey];

    // if payment method is not valid for currently-selected order type, add explanatory sublabel
    // telling user which order types it IS valid for
    if (disabledForSelectedOrderType) {
      const validOrderTypes = useOrderTypesConfigV2
        ? getValidOrderTypesListV2(orderTypesConfig, paymentTypeKey)
        : getValidOrderTypesList(orderTypesConfig, paymentTypeKey);
      sublabel = `(${validOrderTypes.join(', ').replace(/,(?!.*,)/gim, ' & ')} Only)`;
    }

    const option = {
      label: getPaymentMethodLabel(paymentTypeKey),
      sublabel,
      value: getPaymentMethodValue(paymentTypeKey),
      disabled: disabledForSelectedOrderType,
    };

    if (paymentTypeKey === 'linx') {
      option.label = (
        <span key='linx'>
          Linx{' '}
          <a href='https://linx.us/' target='_blank' rel='noopener noreferrer' key='linx-a'>
            ?
          </a>
        </span>
      );
    }
    return option;
  });
  return decoratedPaymentMethods;
}
